import { createTheme } from '@mui/material/styles';
import { createInteractionTransition, INTERACTION_DURATION } from './interactions';

export type ThemeMode = 'light' | 'dark';

// Shared paper-and-green palette for light mode and its dark variant.
export const foundationColors = {
  light: {
    background: '#F6F4EE', surface: '#FFFFFF', primary: '#245444',
    onPrimary: '#FFFFFF', text: '#202B27', muted: '#53615A',
    border: '#D8DED8', selected: '#E6EEE6', subtle: '#F5F7F4', focus: '#B47826',
    success: '#236044', successBackground: '#EAF3EB',
    warning: '#805214', warningBackground: '#FBF1DB',
    error: '#A4362F', errorBackground: '#FBECE9',
  },
  dark: {
    background: '#161D19', surface: '#1E2822', primary: '#A4D0B4',
    onPrimary: '#15251B', text: '#EDF2EE', muted: '#B4C2B9',
    border: '#425349', selected: '#31483B', subtle: '#253129', focus: '#E3B772',
    success: '#B3DBBA', successBackground: '#2B4231',
    warning: '#E9C88A', warningBackground: '#44371F',
    error: '#F3BAB3', errorBackground: '#4B2B27',
  },
} as const;

export const createFoundationTheme = (mode: ThemeMode) => {
  const colors = foundationColors[mode];
  const focus = { outline: `3px solid ${colors.focus}`, outlineOffset: 3 };
  const weaveColor = mode === 'dark' ? 'rgba(211, 226, 192, .055)' : 'rgba(99, 116, 85, .055)';
  const textureLayers = [
    `repeating-linear-gradient(0deg, transparent 0 6px, ${weaveColor} 6px 7px)`,
    `repeating-linear-gradient(90deg, transparent 0 6px, ${weaveColor} 6px 7px)`,
  ];
  const ambientBackground = mode === 'dark'
    ? ['radial-gradient(ellipse at 95% 4%, rgba(65, 107, 72, .34), transparent 58%)', 'radial-gradient(ellipse at 18% 94%, rgba(141, 115, 78, .1), transparent 55%)']
    : ['radial-gradient(ellipse at 95% 4%, rgba(173, 200, 173, .34), transparent 58%)', 'radial-gradient(ellipse at 18% 94%, rgba(223, 199, 165, .2), transparent 55%)'];
  const bodyBackgroundImages = [...textureLayers, ...ambientBackground].join(', ');
  const bodyBackgroundSizes = ['7px 7px', '7px 7px', 'auto', 'auto'].join(', ');

  return createTheme({
    palette: {
      mode: mode === 'dark' ? 'dark' : 'light',
      primary: { main: colors.primary, contrastText: colors.onPrimary },
      secondary: { main: colors.muted },
      background: { default: colors.background, paper: colors.surface },
      text: { primary: colors.text, secondary: colors.muted },
      divider: colors.border,
      action: { hover: colors.subtle, selected: colors.selected },
      success: { main: colors.success },
      warning: { main: colors.warning },
      error: { main: colors.error },
      info: { main: mode === 'dark' ? '#A7C5E1' : '#294D70' },
    },
    typography: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      h1: { fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2 },
      h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2 },
      h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
      h4: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.025em' },
      h5: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
      h6: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
      body1: { fontSize: '1rem', lineHeight: 1.5 },
      body2: { fontSize: '0.875rem', lineHeight: 1.5 },
      button: { fontSize: '0.9375rem', fontWeight: 500, textTransform: 'none' },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontVariantNumeric: 'tabular-nums',
            backgroundColor: colors.background,
            backgroundImage: bodyBackgroundImages,
            backgroundSize: bodyBackgroundSizes,
            backgroundAttachment: 'fixed',
          },
          'a:focus-visible, button:focus-visible, select:focus-visible': focus,
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 48,
            borderRadius: 6,
            padding: '11px 18px',
            transition: createInteractionTransition(theme, ['background-color', 'border-color', 'box-shadow', 'color', 'opacity']),
            '&.Mui-focusVisible': focus,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }),
          sizeSmall: { minHeight: 44, padding: '8px 12px', fontSize: '0.875rem' },
          outlined: { borderColor: colors.border },
          outlinedError: { borderColor: colors.error },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            minWidth: 44,
            minHeight: 44,
            transition: createInteractionTransition(theme, ['background-color', 'color', 'opacity']),
            '&.Mui-focusVisible': focus,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }),
        },
      },
      MuiFab: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: createInteractionTransition(theme, ['background-color', 'box-shadow', 'color', 'opacity']),
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: createInteractionTransition(theme, ['background-color', 'border-color', 'color']),
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }),
        },
      },
      MuiInputBase: { styleOverrides: { input: { '&::placeholder': { color: colors.muted, opacity: 1 } } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: 48, borderRadius: 6, backgroundColor: colors.surface,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary, borderWidth: 2 },
            '&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: colors.error },
          },
          notchedOutline: { borderColor: colors.border },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { color: colors.muted, '&.Mui-focused': { color: colors.primary } } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiDialog: { styleOverrides: { paper: { border: `1px solid ${colors.border}`, borderRadius: 12 } } },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: '1.5rem', fontWeight: 600 } } },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 48,
            transition: createInteractionTransition(theme, ['background-color', 'color']),
            '&.Mui-focusVisible': focus,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 48,
            transition: createInteractionTransition(theme, ['background-color', 'color']),
            '&.Mui-focusVisible': focus,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }),
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          focusHighlight: {
            transition: `opacity ${INTERACTION_DURATION}ms cubic-bezier(0, 0, 0.2, 1)`,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: colors.border, fontSize: '0.875rem', padding: '16px' },
          head: { backgroundColor: colors.subtle, color: colors.muted, fontWeight: 600 },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: createInteractionTransition(theme, ['background-color']),
            '&:not(.MuiTableRow-head):not(.MuiTableRow-footer):not(.Mui-selected):hover': {
              backgroundColor: theme.palette.action.hover,
            },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }),
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            color: colors.muted,
            '&:hover': { color: colors.text },
            '&.Mui-active': {
              color: colors.text,
              '& .MuiTableSortLabel-icon': { color: colors.primary },
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardSuccess: { color: colors.success, backgroundColor: colors.successBackground },
          standardWarning: { color: colors.warning, backgroundColor: colors.warningBackground },
          standardError: { color: colors.error, backgroundColor: colors.errorBackground },
        },
      },
    },
  });
};
